// Central API client (axios). Attaches the app JWT (when present) and handles 401 -> login.
// Auth is active only when REACT_APP_GOOGLE_CLIENT_ID is set (deploy); empty = local dev, no auth.
import axios from "axios";

const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8010";
const AUTH_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

export const api = axios.create({ baseURL });

// Attach the bearer token + signal user activity (feeds the inactivity timer in auth.js).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (!(config.url || "").includes("/auth/refresh")) {
    window.dispatchEvent(new CustomEvent("api-activity"));
  }
  return config;
});

// On 401, drop the token and bounce to the login screen — except on the /auth/ endpoints
// themselves, whose 401/403 are shown inline by the Login component.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    if (err.response?.status === 401 && AUTH_ENABLED && !url.includes("/auth/")) {
      localStorage.removeItem("token");
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

export const EXPORT_URLS = {
  tidy: (exp) => `${baseURL}/api/export/tidy.csv${exp ? `?experiment_id=${exp}` : ""}`,
  runPairs: (exp) => `${baseURL}/api/export/run-pairs.csv${exp ? `?experiment_id=${exp}` : ""}`,
  full: (exp) => `${baseURL}/api/export/full.json${exp ? `?experiment_id=${exp}` : ""}`,
};

// Per-experiment CSVs are AUTHENTICATED downloads. A raw <a href> can't send the Bearer token, so it
// 401s ("Could not validate credentials") when auth is on — fetch through axios (token attached) as a
// blob and save it client-side instead. (The EXPORT_URLS links above share this limitation, hence they
// only work with auth off.)
export const EXPORT_PATHS = {
  experimentRuns: (exp) => `/api/export/experiment-runs.csv?experiment_id=${exp}`,
  experimentStats: (exp) => `/api/export/experiment-stats.csv?experiment_id=${exp}`,
};

export async function downloadExport(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const j = (p) => p.then((r) => r.data);

// auth
export const getMe = () => j(api.get("/auth/me"));
export const googleLogin = (idToken) => j(api.post("/auth/google", { id_token: idToken }));
export const refreshSession = (token) => j(api.post("/auth/refresh", { token }));

// prompts + model config
export const getPrompts = () => j(api.get("/api/prompts"));
export const updatePrompt = (kind, content) => j(api.put("/api/prompts", { kind, content }));
export const updateModelConfig = (roles) => j(api.put("/api/prompts/model-config", { roles }));
export const setLock = (locked) => j(api.post("/api/prompts/lock", { locked }));

// cases
export const getCases = () => j(api.get("/api/cases"));
export const createCase = (body) => j(api.post("/api/cases", body));
export const getCase = (id) => j(api.get(`/api/cases/${id}`));
export const updateCase = (id, body) => j(api.put(`/api/cases/${id}`, body));
export const deleteCase = (id) => j(api.delete(`/api/cases/${id}`));
export const generateCases = (body) => j(api.post("/api/cases/generate", body));

// chief-complaint "Red Flags" (shared do-not-miss rubric per complaint)
export const listCcRubrics = () => j(api.get("/api/cc-rubrics"));
export const updateCcRubric = (chief_complaint, red_flags) => j(api.put("/api/cc-rubrics", { chief_complaint, red_flags }));

// runs + jobs + experiments
export const listRuns = (params = {}) => j(api.get("/api/runs", { params }));
export const getRun = (id) => j(api.get(`/api/runs/${id}`));
export const deleteRun = (id) => j(api.delete(`/api/runs/${id}`));
export const gradeRun = (id) => j(api.post(`/api/runs/${id}/grade`));
// Root-cause re-analyze: returns at once ({status: "pending"} or {status: "unsupported", reason}); poll
// getRun until payload.rootcause_json.status is complete | partial | error (RunDetail, every 3 s).
export const reanalyzeRun = (id) => j(api.post(`/api/runs/${id}/rootcause`));
export const launchRun = (body) => j(api.post("/api/runs/launch", body));
export const launchExperiment = (body) => j(api.post("/api/experiments/launch", body));
export const listExperiments = () => j(api.get("/api/experiments"));
export const deleteExperiment = (id) => j(api.delete(`/api/experiments/${id}`));
export const updateExperiment = (id, name) => j(api.put(`/api/experiments/${id}`, { name }));
export const getJob = (id) => j(api.get(`/api/jobs/${id}`));
export const listJobs = () => j(api.get("/api/jobs"));
export const cancelJob = (id) => j(api.post(`/api/jobs/${id}/cancel`));
export const systemFamilies = (system) => j(api.get(`/api/systems/${system}/families`));

// poll a job until terminal
export async function pollJob(jobId, { interval = 2500, onTick } = {}) {
  for (;;) {
    const jb = await getJob(jobId);
    if (onTick) onTick(jb);
    if (["complete", "error", "blocked", "cancelled"].includes(jb.status)) return jb;
    await new Promise((res) => setTimeout(res, interval));
  }
}

export default api;
